import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Alert
} from 'reactstrap';

interface Company {
  id: string;
  company_name: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string;
  email: string;
  website?: string;
  ein?: string;
  ssn?: string;
  entity_type?: string;
  entity_type_display?: string;
  year_of_formation: number | string;
  full_address?: string;
  created_at: string;
  customer_info?: {
    id: string;
    user_id: string;
    name: string;
    email: string;
    phone: string;
  };
}

interface CustomerDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  customerId: string | null;
  customerName: string;
  customerEmail: string;
}

// Masking functions
const maskEIN = (ein: string): string => {
  if (!ein) return '';
  const numbers = ein.replace(/\D/g, '');
  if (numbers.length < 9) return ein;
  return `**-***${numbers.slice(-4)}`;
};

const maskSSN = (ssn: string): string => {
  if (!ssn) return '';
  const numbers = ssn.replace(/\D/g, '');
  if (numbers.length < 9) return ssn;
  return `***-**-${ssn.slice(-4)}`;
};

const formatPhoneForDisplay = (value: string): string => {
  if (!value) return '';
  const numbers = value.replace(/\D/g, '');
  if (numbers.length <= 3) return numbers;
  if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
  return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
};

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({
  isOpen,
  toggle,
  customerId,
  customerName,
  customerEmail
}) => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchCustomerCompanies = async () => {
    if (!customerId) return;

    try {
      setLoading(true);
      const response = await axios.get('/accounts/companies/', { withCredentials: true });
      if (response.status === 'success') {
        // Filter companies for this customer
        const customerCompanies = response.companies.filter(
          (company: Company) => company.customer_info?.id === customerId
        );
        setCompanies(customerCompanies);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching customer companies:', error);
    }
  };

  useEffect(() => {
    if (isOpen && customerId) {
      fetchCustomerCompanies();
    }
  }, [isOpen, customerId]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>
        <i className="bx bx-user me-2"></i>
        Customer Details - {customerName}
      </ModalHeader>
      <ModalBody>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-4">
              <h6 className="text-muted mb-2">Customer Information</h6>
              <div className="d-flex gap-3">
                <div>
                  <i className="bx bx-envelope me-2 text-muted"></i>
                  {customerEmail}
                </div>
              </div>
            </div>

            <div className="border-top pt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  <i className="bx bx-buildings me-2"></i>
                  Companies ({companies.length})
                </h6>
              </div>

              {companies.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bx bx-buildings font-size-24 text-muted mb-3 d-block"></i>
                  <p className="text-muted mb-0">This customer has not added any companies yet.</p>
                </div>
              ) : (
                <Row>
                  {companies.map((company) => (
                    <Col md={6} key={company.id} className="mb-3">
                      <Card className="border h-100 shadow-sm">
                        <CardBody>
                          <div className="mb-3">
                            <h5 className="mb-1 text-dark">
                              <i className="bx bx-buildings me-2 text-primary"></i>
                              {company.company_name}
                            </h5>
                            <span className="badge bg-soft-primary text-primary font-size-11">
                              Est. {company.year_of_formation}
                            </span>
                          </div>

                          <div className="company-details">
                            <div className="mb-2">
                              <i className="bx bx-envelope me-2 text-muted"></i>
                              <a href={`mailto:${company.email}`} className="text-body">
                                {company.email}
                              </a>
                            </div>
                            <div className="mb-2">
                              <i className="bx bx-phone me-2 text-muted"></i>
                              <a href={`tel:${company.phone}`} className="text-body">
                                {formatPhoneForDisplay(company.phone)}
                              </a>
                            </div>
                            <div className="mb-2">
                              <i className="bx bx-map me-2 text-muted"></i>
                              <span className="text-muted">{company.full_address}</span>
                            </div>
                            {company.website && (
                              <div className="mb-2">
                                <i className="bx bx-globe me-2 text-muted"></i>
                                <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-body">
                                  {company.website}
                                </a>
                              </div>
                            )}
                            {(company.ein || company.ssn || company.entity_type_display) && (
                              <div className="mt-3 pt-3 border-top">
                                {company.entity_type_display && (
                                  <div className="d-inline-block me-3 mb-2">
                                    <small className="text-muted d-block">Company Type</small>
                                    <span className="font-size-13">{company.entity_type_display}</span>
                                  </div>
                                )}
                                {company.ein && (
                                  <div className="d-inline-block me-3 mb-2">
                                    <small className="text-muted d-block">EIN</small>
                                    <span className="font-size-13 font-monospace">{maskEIN(company.ein)}</span>
                                  </div>
                                )}
                                {company.ssn && (
                                  <div className="d-inline-block mb-2">
                                    <small className="text-muted d-block">SSN</small>
                                    <span className="font-size-13 font-monospace">{maskSSN(company.ssn)}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="mt-3 pt-2 border-top">
                            <small className="text-muted">
                              <i className="bx bx-time-five me-1"></i>
                              Created{' '}
                              {new Date(company.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </small>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
};

export default CustomerDetailModal;

